import React, { useState, useCallback } from 'react';
import {
    Button,
    Link,
    IconButton,
    makeStyles,
    Tooltip,
} from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import PdfViewerModalV2, { PdfViewerModalV2Props } from './PdfViewerModalV2';
import { isPdfLink } from '../../../utils/appHelper';

const useStyles = makeStyles((theme) => ({
    button: {
        textTransform: 'none',
    },
    link: {
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
}));

export type PdfViewerButtonVariant = 'button' | 'link' | 'icon';

export interface PdfViewerButtonProps
    extends Omit<PdfViewerModalV2Props, 'open' | 'onClose'> {
    /** Button variant: 'button', 'link', or 'icon' */
    variant?: PdfViewerButtonVariant;
    /** Button text (for button variant) */
    buttonText?: string;
    /** Link text (for link variant) */
    linkText?: string;
    /** Button color (Material-UI color) */
    color?: 'default' | 'inherit' | 'primary' | 'secondary';
    /** Button size */
    size?: 'small' | 'medium' | 'large';
    /** Icon to display (for icon variant) */
    icon?: React.ReactNode;
    /** Tooltip text (for icon variant) */
    tooltip?: string;
    /** Custom className */
    className?: string;
    /** Custom style */
    style?: React.CSSProperties;
    /** Whether to show icon in button/link */
    showIcon?: boolean;
    /** Icon position: 'start' or 'end' */
    iconPosition?: 'start' | 'end';
    /** Custom onClick handler (will still open modal) */
    onClick?: (event: React.MouseEvent) => void;
    onClose?: () => void
}

const PdfViewerButton: React.FC<PdfViewerButtonProps> = ({
    variant = 'button',
    buttonText = 'View PDF',
    linkText = 'View PDF',
    color = 'primary',
    size = 'medium',
    icon,
    tooltip,
    className,
    style,
    showIcon = true,
    iconPosition = 'start',
    onClick,
    onClose,
    file,
    ...modalProps
}) => {
    const classes = useStyles();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const handleOpen = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            onClick?.(event);
            // If `file` is a string URL and not a valid PDF,
            // open it in a new browser tab instead of the PDF modal.
            if (typeof file === 'string') {
                if (!isPdfLink(file)) {
                    window.open(file, '_blank', 'noopener,noreferrer');
                    return;
                }
            }

            setIsModalOpen(true);
        },
        [onClick, file]
    );

    const handleClose = useCallback(() => {
        setIsModalOpen(false);
        onClose?.();
    }, [onClose]);

    const getDefaultIcon = () => {
        if (icon) return icon;
        if (variant === 'icon') {
            return <PictureAsPdfIcon />;
        }
        return <DescriptionIcon />;
    };

    const renderIcon = () => {
        if (!showIcon) return null;
        const iconElement = getDefaultIcon();
        return iconPosition === 'start' ? iconElement : null;
    };

    const renderEndIcon = () => {
        if (!showIcon || iconPosition !== 'end') return null;
        return getDefaultIcon();
    };

    const renderContent = () => {
        switch (variant) {
            case 'link':
                return (
                    <Link
                        component="button"
                        variant="body1"
                        onClick={handleOpen}
                        className={`${classes.link} ${className || ''}`}
                        style={style}
                    >
                        {renderIcon()}
                        {linkText}
                        {renderEndIcon()}
                    </Link>
                );

            case 'icon':
                const iconButton = (
                    <IconButton
                        onClick={handleOpen}
                        color={color}
                        size={size === 'large' ? 'medium' : size}
                        className={className}
                        style={style}
                    >
                        {getDefaultIcon()}
                    </IconButton>
                );

                if (tooltip) {
                    return (
                        <Tooltip title={tooltip}>
                            <span>{iconButton}</span>
                        </Tooltip>
                    );
                }

                return iconButton;

            case 'button':
            default:
                return (
                    <Button
                        variant="contained"
                        color={color}
                        size={size}
                        onClick={handleOpen}
                        className={`${classes.button} ${className || ''}`}
                        style={style}
                        startIcon={iconPosition === 'start' ? renderIcon() : undefined}
                        endIcon={iconPosition === 'end' ? renderEndIcon() : undefined}
                    >
                        {buttonText}
                    </Button>
                );
        }
    };

    return (
        <>
            {renderContent()}
            {!!isModalOpen && <PdfViewerModalV2
                open={isModalOpen}
                onClose={handleClose}
                file={file}
                {...modalProps}
            />}
        </>
    );
};

export default PdfViewerButton;
